/**
 * GrammarPopover — floating popover for spell + grammar suggestions.
 *
 * Listens for a single `suggestion-click` event dispatched by the unified
 * inline-check extension. The match's `kind` decides the header copy:
 *   - spell -> "Spelling"
 *   - grammar -> the LT short/long message
 *
 * Replaces a misspelling / replaces the matched text with the chosen
 * suggestion. Dismiss button + click-outside / scroll closes the popover.
 */

import * as Popover from "@radix-ui/react-popover";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import type { GrammarMatch } from "../../../lib/tipTapExtensions/grammarEngine";
import {
  GRAMMAR_SUGGESTION_CANCEL_CLOSE_EVENT,
  GRAMMAR_SUGGESTION_CLOSE_EVENT,
  GRAMMAR_SUGGESTION_OPEN_EVENT,
} from "../../../lib/tipTapExtensions/grammarEvents";
import { sendAppEvent } from "src/Utils";
import Auth from "src/Auth";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import IconButton from "src/components/IconButton";

interface SuggestionClickDetail {
  match: GrammarMatch;
  from: number;
  to: number;
  x: number;
  y: number;
  anchorRect?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

interface GrammarPopoverProps {
  editor: any;
}

function headerCopy(match: GrammarMatch | null): string {
  if (!match) return "Suggestion";
  if (match.kind === "spell") return "Spelling";
  return match.shortMessage || match.message || "Grammar suggestion";
}

const HOVER_CLOSE_DELAY_MS = 120;
const TEXT_CHANGING_KEYS = new Set(["Backspace", "Delete", "Enter"]);

function shouldCloseForEditorKeydown(event: KeyboardEvent): boolean {
  if (event.isComposing) return false;
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  if (event.key.length === 1) return true;
  return TEXT_CHANGING_KEYS.has(event.key);
}

export function GrammarPopover({ editor }: GrammarPopoverProps) {
  const openStyleSettings = useSudowriteStore((s: any) => s.openStyleSettings);
  const [open, setOpen] = useState(false);
  const [match, setMatch] = useState<GrammarMatch | null>(null);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);
  const [anchorRect, setAnchorRect] = useState({
    top: 0,
    left: 0,
    width: 1,
    height: 1,
  });
  const [dismissPending, setDismissPending] = useState(false);
  const [dismissError, setDismissError] = useState<string | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popoverHoveredRef = useRef(false);

  const cancelScheduledClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const closePopover = useCallback(() => {
    cancelScheduledClose();
    popoverHoveredRef.current = false;
    setOpen(false);
  }, [cancelScheduledClose]);

  const scheduleHoverClose = useCallback(() => {
    cancelScheduledClose();
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      if (!popoverHoveredRef.current) {
        setOpen(false);
      }
    }, HOVER_CLOSE_DELAY_MS);
  }, [cancelScheduledClose]);

  const handleOpen = useCallback((e: Event) => {
    const detail = (e as CustomEvent<SuggestionClickDetail>).detail;
    if (!detail) return;

    const detailAnchorRect = detail.anchorRect;
    setMatch(detail.match);
    setFrom(detail.from);
    setTo(detail.to);
    // detail.x/y and anchorRect come from same-document viewport
    // coordinates now that the editor is no longer inside an iframe.
    if (
      detailAnchorRect &&
      Number.isFinite(detailAnchorRect.x) &&
      Number.isFinite(detailAnchorRect.y) &&
      Number.isFinite(detailAnchorRect.width) &&
      Number.isFinite(detailAnchorRect.height)
    ) {
      setAnchorRect({
        top: detailAnchorRect.y,
        left: detailAnchorRect.x,
        width: Math.max(1, detailAnchorRect.width),
        height: Math.max(1, detailAnchorRect.height),
      });
    } else {
      setAnchorRect({
        top: detail.y,
        left: detail.x,
        width: 1,
        height: 1,
      });
    }
    cancelScheduledClose();
    setDismissError(null);
    setDismissPending(false);
    setOpen(true);
  }, [cancelScheduledClose]);

  const handleHoverClose = useCallback(() => {
    scheduleHoverClose();
  }, [scheduleHoverClose]);

  const handleCancelHoverClose = useCallback(() => {
    cancelScheduledClose();
  }, [cancelScheduledClose]);

  useEffect(() => {
    // Suggestion events bubble from the editor marks to the main document.
    document.addEventListener("suggestion-click", handleOpen);
    document.addEventListener(GRAMMAR_SUGGESTION_OPEN_EVENT, handleOpen);
    document.addEventListener(GRAMMAR_SUGGESTION_CLOSE_EVENT, handleHoverClose);
    document.addEventListener(GRAMMAR_SUGGESTION_CANCEL_CLOSE_EVENT, handleCancelHoverClose);

    return () => {
      document.removeEventListener("suggestion-click", handleOpen);
      document.removeEventListener(GRAMMAR_SUGGESTION_OPEN_EVENT, handleOpen);
      document.removeEventListener(GRAMMAR_SUGGESTION_CLOSE_EVENT, handleHoverClose);
      document.removeEventListener(GRAMMAR_SUGGESTION_CANCEL_CLOSE_EVENT, handleCancelHoverClose);
    };
  }, [handleOpen, handleHoverClose, handleCancelHoverClose]);

  useEffect(() => {
    return () => {
      cancelScheduledClose();
    };
  }, [cancelScheduledClose]);

  useEffect(() => {
    if (!open) return;

    const handleMainClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      // Editor mousedowns can target text nodes — normalize to an element.
      const targetElement =
        target?.nodeType === Node.ELEMENT_NODE
          ? (target as HTMLElement)
          : target?.parentElement ?? null;
      if (targetElement?.closest?.("[data-grammar-popover]")) return;
      // Clicks inside the editor close the popover unless they land on a
      // spell/grammar mark (that mark's own handler decides what opens next).
      const container = document.getElementById("editorContainer");
      if (container && target && container.contains(target)) {
        if (targetElement?.closest?.(".spell-error, .grammar-error")) return;
      }
      closePopover();
    };

    document.addEventListener("mousedown", handleMainClick);

    return () => {
      document.removeEventListener("mousedown", handleMainClick);
    };
  }, [open, closePopover]);

  useEffect(() => {
    if (!open) return;

    const handleScroll = () => closePopover();

    // Capture-phase document listener sees scrolls of every scroll container,
    // including the editor's.
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [open, closePopover]);

  useEffect(() => {
    if (!open) return;

    const handleEditorTyping = () => closePopover();
    const handleEditorKeydown = (event: KeyboardEvent) => {
      if (shouldCloseForEditorKeydown(event)) {
        closePopover();
      }
    };
    const container = document.getElementById("editorContainer");
    if (!container) return;

    container.addEventListener("beforeinput", handleEditorTyping);
    container.addEventListener("input", handleEditorTyping);
    container.addEventListener("keydown", handleEditorKeydown);

    return () => {
      container.removeEventListener("beforeinput", handleEditorTyping);
      container.removeEventListener("input", handleEditorTyping);
      container.removeEventListener("keydown", handleEditorKeydown);
    };
  }, [open, closePopover]);

  const handleSuggestionClick = (suggestion: string, index: number) => {
    if (editor?.commands?.replaceMatch) {
      editor.commands.replaceMatch(from, to, suggestion);
    }
    const kind = match?.kind ?? "grammar";
    const event =
      kind === "spell"
        ? "Spellcheck - Suggestion Selected"
        : "Grammar - Suggestion Selected";
    void sendAppEvent(event, {
      rule_id: match?.ruleId,
      suggestion_index: index,
    });
    closePopover();
  };

  /**
   * Get the matched word/phrase from the editor at [from, to]. Used to
   * populate the dictionary word (spell) and the `matchText` filter
   * (grammar). Falls back to "" if the editor is not available.
   */
  const matchedText = (): string => {
    if (!editor?.state?.doc) return "";
    try {
      return editor.state.doc.textBetween(from, to, " ");
    } catch {
      return "";
    }
  };

  /**
   * Optimistically clears the decoration for this match, fires the
   * legacy analytics event, then persists the dismissal to the backend.
   * On success the popover closes. On failure we surface a quiet error
   * — the editor will re-fetch on the next debounce tick and re-decorate
   * if the persistence call did not land.
   */
  const performDismiss = async (
    endpoint: string,
    body: Record<string, unknown>,
    analyticsEvent: string
  ) => {
    if (!match || dismissPending) return;
    setDismissPending(true);
    setDismissError(null);

    // Optimistic UI: remove the decoration immediately.
    if (editor?.commands?.dismissMatch) {
      editor.commands.dismissMatch(from, to, match.ruleId);
    }

    void sendAppEvent(analyticsEvent, {
      rule_id: match.ruleId,
      kind: match.kind,
    });

    try {
      await Auth.fetchWithIdToken(endpoint, body);
      // Mirror the persisted change into the editor's in-memory
      // preferences cache so the very next /check filter pass also
      // suppresses the phrase (without a /preferences round-trip).
      if (endpoint === "/api/grammar/style-rule") {
        if (editor?.commands?.addLocalStyleRule) {
          editor.commands.addLocalStyleRule({
            rule_id: body.ruleId,
            action: body.action,
            match_text: body.matchText ?? null,
            document_id: body.documentId ?? null,
          });
        }
      }
      if (endpoint === "/api/grammar/dictionary") {
        if (editor?.commands?.addLocalDictionaryWord) {
          editor.commands.addLocalDictionaryWord(String(body.word ?? ""));
        }
      }
      // Now that the new dictionary word / style rule is persisted, force
      // a full-doc recheck so other occurrences of the same word/phrase
      // also lose their underline immediately — without waiting for the
      // user to edit those paragraphs.
      if (editor?.commands?.recheckGrammarFull) {
        editor.commands.recheckGrammarFull();
      }
      closePopover();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[grammar popover] dismiss failed:", err);
      setDismissError("Couldn't save — please try again");
      // Leave the popover open so the user sees the error. The underline
      // will be restored on the next /check round-trip if the write
      // didn't persist.
    } finally {
      setDismissPending(false);
    }
  };

  const handleIgnore = () => {
    if (!match) return;
    const documentId = editor?.storage?.grammar?.documentId ?? undefined;
    // SW-9064: persist the LT-provided rule description (e.g.,
    // "Personal pronoun agreement") so the Style Settings dialog can
    // show a human-readable label instead of the bare rule id.
    const ruleDescription =
      match.shortMessage || match.ruleDescription || match.message || undefined;
    void performDismiss(
      "/api/grammar/style-rule",
      {
        ruleId: match.ruleId,
        action: "off",
        matchText: matchedText(),
        ...(ruleDescription ? { ruleDescription } : {}),
        ...(documentId ? { documentId } : {}),
      },
      "Grammar - Dismissed"
    );
  };

  /**
   * SW-9064: spell-error dismissal is now a true "Add to dictionary"
   * (POST /api/grammar/dictionary) rather than a style-rule ignore.
   * Spell + grammar paths diverged so users can curate the dictionary
   * from a single screen.
   */
  const handleAddToDictionary = () => {
    if (!match) return;
    void performDismiss(
      "/api/grammar/dictionary",
      { word: matchedText() },
      "Spellcheck - Added to dictionary"
    );
  };

  return (
    <Popover.Root open={open}>
      <Popover.Anchor asChild>
        <div
          data-grammar-popover-anchor
          style={{
            position: "fixed",
            top: anchorRect.top,
            left: anchorRect.left,
            width: anchorRect.width,
            height: anchorRect.height,
            pointerEvents: "none",
          }}
        />
      </Popover.Anchor>

      <AnimatePresence>
        {open && (
          <Popover.Portal forceMount>
            <Popover.Content
              data-grammar-popover
              data-suggestion-kind={match?.kind ?? "grammar"}
              side="bottom"
              align="start"
              sideOffset={6}
              onOpenAutoFocus={(e) => e.preventDefault()}
              forceMount
              asChild
            >
              <StyledPopoverContent
                initial={{ opacity: 0, scale: 0.96, y: -2 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: -2 }}
                transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                onMouseEnter={() => {
                  popoverHoveredRef.current = true;
                  cancelScheduledClose();
                }}
                onMouseLeave={() => {
                  popoverHoveredRef.current = false;
                  scheduleHoverClose();
                }}
              >
                <div className="grammar-header">
                  <span className="grammar-message">{headerCopy(match)}</span>
                </div>

                {match && match.replacements.length > 0 ? (
                  <ul className="suggestions-list">
                    {match.replacements.slice(0, 5).map((sug, i) => (
                      <li key={`${sug}-${i}`}>
                        <button
                          className="suggestion-button"
                          onClick={() => handleSuggestionClick(sug, i)}
                        >
                          {sug}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="no-suggestions">No suggestions</div>
                )}

                {match && (
                  <div className="grammar-footer">
                    <IconButton
                      size={32}
                      iconSize={16}
                      src="/assets/gear-v2.svg"
                      smartFill
                      aria-label="Style settings"
                      onClick={() => {
                        openStyleSettings("spelling-and-grammar");
                        closePopover();
                      }}
                    />
                    {match.kind === "spell" ? (
                      <button
                        type="button"
                        className="dismiss-button"
                        onClick={handleAddToDictionary}
                        disabled={dismissPending}
                      >
                        Add to dictionary
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="dismiss-button"
                        onClick={handleIgnore}
                        disabled={dismissPending}
                      >
                        Ignore
                      </button>
                    )}
                  </div>
                )}
                {dismissError && (
                  <div className="grammar-error-msg" role="status">
                    {dismissError}
                  </div>
                )}
              </StyledPopoverContent>
            </Popover.Content>
          </Popover.Portal>
        )}
      </AnimatePresence>
    </Popover.Root>
  );
}

const StyledPopoverContent = styled(motion.div)`
  z-index: 100;
  background: var(--menu-color);
  border: 1px solid var(--menu-color-hover);
  border-radius: 12px;
  box-shadow: var(--card-drop-shadow);
  padding: 6px;
  min-width: 220px;
  max-width: 320px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transform-origin: top left;

  .grammar-header {
    padding: 6px 10px 4px;

    .grammar-message {
      color: var(--grey-1);
      font-size: 12px;
      font-weight: 500;
      line-height: 1.4;
      display: block;
      text-wrap: pretty;
    }
  }

  .suggestions-list {
    list-style: none;
    margin: 0;
    padding: 2px 0;

    li {
      margin: 0;
    }
  }

  .suggestion-button {
    display: block;
    width: 100%;
    text-align: left;
    padding: 8px 10px;
    background: none;
    border: none;
    color: var(--grey-0);
    font-family: inherit;
    font-size: 14px;
    line-height: 1.3;
    cursor: pointer;
    border-radius: 6px;
    transition: background-color 150ms cubic-bezier(0.2, 0, 0, 1),
      color 150ms cubic-bezier(0.2, 0, 0, 1),
      transform 150ms cubic-bezier(0.2, 0, 0, 1);

    &:hover,
    &:focus-visible {
      background: var(--menu-color-hover);
      color: var(--grey-0);
      outline: none;
    }

    &:active {
      transform: scale(0.96);
    }
  }

  .no-suggestions {
    padding: 8px 10px;
    color: var(--grey-2);
    font-size: 13px;
  }

  .grammar-footer {
    margin-top: 2px;
    padding-top: 2px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .dismiss-button {
    background: none;
    border: none;
    color: var(--grey-2);
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 6px;
    transition: background-color 150ms cubic-bezier(0.2, 0, 0, 1),
      color 150ms cubic-bezier(0.2, 0, 0, 1),
      transform 150ms cubic-bezier(0.2, 0, 0, 1);

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      background: var(--menu-color-hover);
      color: var(--grey-0);
      outline: none;
    }

    &:active:not(:disabled) {
      transform: scale(0.96);
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  .grammar-error-msg {
    padding: 4px 10px 6px;
    color: var(--danger-color, #ff5862);
    font-size: 12px;
  }
`;

export default GrammarPopover;
